import moment from 'moment';
import { useToast } from 'src/context/ToastProvider';
import { useMutation, useQueryClient } from 'react-query';
import React, { FormEvent, useEffect, useState } from 'react';
import { UpdateProjectDetailsInput, UserProjectDetail } from 'types';
import { createUserProjectFn, editUserProjectFn } from 'src/apis/profile';
import { Button, Label, Modal, Spinner, TextInput, Textarea } from 'flowbite-react';

type Props = {
	isOpen: boolean;
	onClose: () => void;
	projectDetails: UserProjectDetail;
	isEditing?: boolean;
	selectedProfileId: string;
};

const EditProfileProject = ({ selectedProfileId, isOpen, onClose, isEditing, projectDetails }: Props) => {
	const toast = useToast();
	const queryClient = useQueryClient();
	const [projectId, setProjectId] = useState('');
	const [dateError, setDateError] = useState<string>('');
	const [userProjectDetails, setUserProjectDetails] = useState<UpdateProjectDetailsInput>({
		updateInfo: { description: '', endedAt: new Date(), link: '', startedFrom: new Date(), title: '' },
	});

	const editProjectDetailsFn = useMutation({
		mutationKey: 'editProjectDetail',
		mutationFn: editUserProjectFn,
		onSuccess(data) {
			queryClient.refetchQueries(['getUserProfile']);
			toast({
				title: 'Project detail updated successfully',
				duration: 3000,
				type: 'success',
			});
			onClose();
		},
	});

	const createProjectDetailsFn = useMutation({
		mutationKey: 'addProjectDetail',
		mutationFn: createUserProjectFn,
		onSuccess(data) {
			queryClient.refetchQueries(['getUserProfile']);
			toast({
				title: 'Project detail added successfully',
				duration: 3000,
				type: 'success',
			});
			onClose();
		},
	});

	const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		if (userProjectDetails.updateInfo.endedAt && userProjectDetails.updateInfo.startedFrom) {
			if (userProjectDetails.updateInfo.startedFrom > userProjectDetails.updateInfo.endedAt) {
				setDateError('start date must be less than end date');
				return;
			}
		}

		if (isEditing) {
			editProjectDetailsFn.mutate({
				id: projectId || '',
				data: userProjectDetails,
			});
		} else {
			createProjectDetailsFn.mutate(userProjectDetails);
		}
	};

	useEffect(() => {
		setUserProjectDetails({
			updateInfo: {
				description: projectDetails.description || '',
				endedAt: projectDetails.endedAt ? moment(projectDetails.endedAt).toDate() : new Date(),
				link: projectDetails.link || '',
				startedFrom: projectDetails.startedFrom ? moment(projectDetails.startedFrom).toDate() : new Date(),
				title: projectDetails.title || '',
			},
		});
		setProjectId(selectedProfileId || '');
	}, [projectDetails, selectedProfileId]);

	useEffect(() => {
		setDateError('');
	}, [isOpen]);

	return (
		<Modal dismissible={true} show={isOpen} onClose={onClose} size="lg" className="!h-full backdrop-blur-[4px]">
			<Modal.Header className="rounded-t-md border-b-0 dark:bg-[#1F2A37]">
				<div>
					<p className="font-inter text-[20px] font-[700] dark:dark:text-white">Projects</p>
				</div>
			</Modal.Header>
			<form className="dark:dark:text-white" onSubmit={handleSubmit}>
				<Modal.Body className="dark:bg-[#1F2A37] !pt-[8px]">
					<div>
						<div className="mb-[8px]">
							<div className="mb-[5px]">
								<Label className="font-inter text-[14px] font-[400] dark:dark:text-white" htmlFor="name" value="Name" />
								<span className="text-red-600">*</span>
							</div>
							<TextInput
								style={{
									fontFamily: "'Inter', sans-serif",
								}}
								type="text"
								id="title"
								name="name"
								value={userProjectDetails.updateInfo.title || ''}
								onChange={e =>
									setUserProjectDetails({ updateInfo: { ...userProjectDetails.updateInfo, title: e.target.value } })
								}
							/>
						</div>
						<div className="mb-[8px]">
							<div className="mb-[5px]">
								<Label
									className="font-inter text-[14px] font-[400] dark:text-white"
									htmlFor="description"
									value="Description"
								/>
							</div>
							<Textarea
								style={{
									fontFamily: "'Inter', sans-serif",
									height: '90px',
								}}
								id="description"
								name="description"
								value={userProjectDetails.updateInfo.description || ''}
								onChange={e =>
									setUserProjectDetails({
										updateInfo: { ...userProjectDetails.updateInfo, description: e.target.value },
									})
								}
							/>
						</div>
						<div className="mb-[8px]">
							<div className="mb-[5px]">
								<Label className="font-inter text-[14px] font-[400] dark:text-white" htmlFor="link" value="Link" />
								<span className="text-red-600">*</span>
							</div>
							<TextInput
								style={{
									fontFamily: "'Inter', sans-serif",
								}}
								type="url"
								id="link"
								name="link"
								value={userProjectDetails.updateInfo.link || ''}
								onChange={e =>
									setUserProjectDetails({
										updateInfo: {
											...userProjectDetails.updateInfo,
											link: e.target.value,
										},
									})
								}
							/>
						</div>
						<div className="mb-[5px] flex gap-4">
							<div>
								<div className="mb-[5px]">
									<Label
										className="font-inter text-[14px] font-[400] dark:text-white"
										htmlFor="startedFrom"
										value="Start date"
									/>
									<span className="text-red-600">*</span>
								</div>
								<div className="flex gap-[16px]">
									<TextInput
										style={{
											fontFamily: "'Inter', sans-serif",
										}}
										type="date"
										id="startedFrom"
										name="startedFrom"
										value={moment(userProjectDetails.updateInfo.startedFrom).format('YYYY-MM-DD') || ''}
										onChange={e =>
											setUserProjectDetails({
												updateInfo: {
													...userProjectDetails.updateInfo,
													startedFrom: moment(e.target.value).toDate(),
												},
											})
										}
									/>
								</div>
							</div>
							<div>
								<div className="mb-[5px]">
									<Label
										className="font-inter text-[14px] font-[400] dark:text-white"
										htmlFor="endedAt"
										value="End date"
									/>
									<span className="text-red-600">*</span>
								</div>
								<div className="flex gap-[16px]">
									<TextInput
										style={{
											fontFamily: "'Inter', sans-serif",
										}}
										type="date"
										id="endedAt"
										name="endedAt"
										value={moment(userProjectDetails.updateInfo.endedAt).format('YYYY-MM-DD') || ''}
										onChange={e =>
											setUserProjectDetails({
												updateInfo: {
													...userProjectDetails.updateInfo,
													endedAt: moment(e.target.value).toDate(),
												},
											})
										}
									/>
								</div>
							</div>
						</div>
					</div>

					{dateError && <div className="text-xs font-bold text-red-500">{dateError}</div>}
				</Modal.Body>
				<Modal.Footer className="rounded-b-md border-t-0 dark:bg-[#18222D]">
					<div className="flex w-full justify-end">
						<div className="">
							<Button
								color="purplePlaced"
								type="submit"
								disabled={
									!userProjectDetails.updateInfo.title ||
									!userProjectDetails.updateInfo.startedFrom ||
									!userProjectDetails.updateInfo.endedAt ||
									!userProjectDetails.updateInfo.link ||
									editProjectDetailsFn.isLoading ||
									createProjectDetailsFn.isLoading
								}
							>
								{isEditing ? (
									<>{editProjectDetailsFn.isLoading ? <Spinner size="sm" light={true} /> : 'Save'}</>
								) : (
									<>{createProjectDetailsFn.isLoading ? <Spinner size="sm" light={true} /> : 'Save'}</>
								)}
							</Button>
						</div>
					</div>
				</Modal.Footer>
			</form>
		</Modal>
	);
};

export default EditProfileProject;
